import React, { FC, useState } from 'react';
import { message } from 'antd';
import Attachments from '../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Attachments';
import TaskLocation from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/TaskLocation';
import DueDate from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/DueDate';
import { Assignees } from '../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Assignees';
import AdditionalInfo from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/AdditionalInfo';
import Severity from '../../../../clients/sidebar/views/SidebarPanel/Detailbar/Severity';
import Tags from '../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Tags';
import styles from './index.module.css';
import camelCaseKeys from 'camelcase-keys';
import VisibleTo from '../../../../../clients/sidebar/views/SidebarPanel/Detailbar/VisibleTo';
import { v4 as uuidv4 } from 'uuid';
import { Props } from './types';
import { useLinkedTasks } from '../useLinkedTasks';
import LinkedTasks from '../../../../../clients/sidebar/views/SidebarPanel/Detailbar/LinkedTasks';
import { Role } from '../../../../../clients/sidebar/models/User';

export const TaskInfo: FC<Props> = ({
  isRecording,
  taskUrls,
  priorityId,
  setProperty,
  assignees,
  tagIds,
  allTags,
  canCreateTagsViaNewTask,
  tagSources,
  dueDate,
  attachments,
  onAttachmentUploaded,
  bugherdUrl,
  projectId,
  taskId,
  relatedToTaskIds,
  viewLinkedTasks,
  browserData,
  selector,
  taskMetadata,
  isAdminView,
  container,
  editSeverity,
  editAssignees,
  viewAssignees,
  editTags,
  viewDueDate,
  editDueDate,
  viewSelector,
  attachmentsDisabled,
  gotoTask,
  canEditTaskUrl,
  canManageProject,
  sites,
  projectUrl,
  projectDomains,
  pageUrl,
  guestVisible,
  taskEditVisibility,
  projectSiteId,
  assignGuests,
  assignGuestsAllowed,
  billingRights,
  currentUser,
  users,
  guestsSeeGuestsAllowed,
  guestsSeeGuests,
  watcherIds
}) => {
  const [pastedFile, setPastedFile] = useState<File | null>(null);
  const {
    linkedTasks,
    loading: linkedTasksLoading,
    error: linkedTasksError
  } = useLinkedTasks({
    bugherdUrl,
    projectId,
    isAdminView,
    activeTask: {
      relatedToTaskIds,
      id: taskId
    }
  });

  const handleUpdateTags = ids => {
    setProperty({
      tag_ids: ids,
      tag_names: allTags
        .filter(tag => ids.includes(tag.get('id')))
        .map(tag => tag.get('name'))
    });
  };

  const handleCreateTag = newTagName => {
    setProperty({
      tag_ids: [...tagIds, null],
      tag_names: allTags
        .filter(tag => tagIds.includes(tag.get('id')))
        .map(tag => tag.get('name'))
        .concat(newTagName)
    });
  };

  const handleRemoveTag = tagId => {
    const _tagIds = tagIds.filter(id => id !== tagId);
    setProperty({
      tag_ids: _tagIds,
      tag_names: allTags
        .filter(tag => _tagIds.includes(tag.get('id')))
        .map(tag => tag.get('name'))
    });
  };

  const handlePaste = e => {
    const copiedFile = e.clipboardData.files;
    const taskIdAndUid = {
      taskId,
      uid: uuidv4()
    };

    const filesWithProperties = Array.from(copiedFile).map(fileObject => {
      const file = new File([fileObject], fileObject.name, {
        type: fileObject.type,
        lastModified: fileObject.lastModified,
        ...taskIdAndUid
      });

      return file;
    });

    if (filesWithProperties.length > 0) {
      setPastedFile(filesWithProperties);
      message.success('File pasted successfully.');
    }
  };

  const getTags = tag => ({
    id: tag.get('id'),
    name: tag.get('name'),
    active: tag.get('active')
  });

  return (
    <div onPaste={handlePaste} className={styles.container}>
      {taskUrls && (
        <TaskLocation
          gotoTask={gotoTask}
          taskPath={taskUrls.taskPath}
          absoluteUrl={taskUrls.absoluteUrl}
          container={container}
          canEditTaskUrl={canEditTaskUrl}
          taskHost={taskUrls.taskHost}
          canManageProject={canManageProject}
          isAdminView={isAdminView}
          sites={sites}
          devurl={projectUrl}
          projectDomains={projectDomains}
          updateSite={site => setProperty({ site })}
          updateUrl={url => setProperty({ url })}
          taskId={taskId}
          pageUrl={pageUrl}
          projectId={projectId}
          bugherdUrl={bugherdUrl}
          projectSiteId={projectSiteId}
        />
      )}

      <LinkedTasks
        linkedTasks={linkedTasks}
        viewLinkedTasks={viewLinkedTasks}
        container={container}
        loading={taskId ? linkedTasksLoading : false}
        error={linkedTasksError}
        projectId={projectId}
        bugherdUrl={bugherdUrl}
        isAdminView={isAdminView}
        currentUserRole={currentUser.role as Role}
      />

      <Severity
        priorityId={priorityId}
        updateSeverity={value => setProperty({ priority_id: value })}
        canEditSeverity={editSeverity}
      />

      {taskEditVisibility && (
        <VisibleTo
          updateVisibility={visibility =>
            setProperty({ guest_visible: visibility })
          }
          guestVisible={!!guestVisible}
          hasGuestWatchers={watcherIds.some(watcherId =>
            users.find(user => user.id === watcherId && user.role === 'guest')
          )}
        />
      )}

      <Assignees
        container={container}
        canEditAssignees={editAssignees}
        viewAssignees={viewAssignees}
        assignees={assignees}
        updateTask={({
          assigneeIds: assignee_ids,
          guestVisible: guest_visible
        }: {
          assigneeIds?: number[];
          guestVisible?: boolean;
        }) => {
          let properties: {
            assignee_ids?: number[];
            guest_visible?: boolean;
          } = {};
          if (assignee_ids) properties.assignee_ids = assignee_ids;
          if (guest_visible) properties.guest_visible = guest_visible;
          setProperty(properties);
        }}
        assignGuestsAllowed={assignGuestsAllowed}
        bugherdUrl={bugherdUrl}
        assignGuests={assignGuests}
        currentUser={currentUser}
        assignableUsers={
          users &&
          users.filter(
            ({ role, is_on_project }) =>
              role !== 'collaborator' && is_on_project
          )
        }
        billing={billingRights}
        isAdminView={isAdminView}
        guestVisible={!!guestVisible}
        guestsSeeGuestsAllowed={guestsSeeGuestsAllowed}
        guestsSeeGuests={guestsSeeGuests}
      />

      <Tags
        onUpdateTags={handleUpdateTags}
        onCreateTag={handleCreateTag}
        onRemoveTag={handleRemoveTag}
        tags={allTags.filter(tag => tagIds.includes(tag.id)).map(getTags)}
        tagIds={tagIds}
        allTags={allTags.map(getTags)}
        tagSources={tagSources}
        canEditTags={editTags}
        canCreateTag={canCreateTagsViaNewTask}
      />

      {(editDueDate || viewDueDate) && (
        <DueDate
          dueDate={dueDate}
          container={container}
          canEditDueDate={editDueDate}
          updateDueDate={date => setProperty({ due_at: date })}
        />
      )}

      {!attachmentsDisabled && (
        <Attachments
          attachments={attachments}
          onUpdateAttachments={attachments => setProperty({ attachments })}
          onAttachmentUploaded={onAttachmentUploaded}
          bugherdUrl={bugherdUrl}
          projectId={projectId}
          taskId={taskId}
          isAdminView={isAdminView}
          container={container}
          pastedFiles={pastedFile}
        />
      )}

      <AdditionalInfo
        isRecording={isRecording}
        browserData={camelCaseKeys(browserData, { deep: true })}
        selector={selector}
        absoluteUrl={taskUrls ? taskUrls.absoluteUrl : ''}
        canViewSelector={viewSelector}
        metadata={taskMetadata}
        isAdminView={isAdminView}
      />
    </div>
  );
};
